import React, { Suspense, lazy } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BaziProvider } from './contexts/BaziContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import BaziBar from './components/BaziBar';
import RouteGuard from './components/RouteGuard';

// ===== 首屏关键页面（eager load — 用户首先接触）=====
import HomePage from './pages/HomePage';
import InputPage from './pages/InputPage';

// ===== 其余页面按需加载（code splitting）=====
const ResultPage = lazy(() => import('./pages/ResultPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MembershipPage = lazy(() => import('./pages/MembershipPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const DailySignPage = lazy(() => import('./pages/DailySignPage'));
const LearnPage = lazy(() => import('./pages/LearnPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BoneWeightPage = lazy(() => import('./pages/BoneWeightPage'));
const BoneWeightHistoryPage = lazy(() => import('./pages/BoneWeightHistoryPage'));
const TimeFixPage = lazy(() => import('./pages/TimeFixPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DataManagementPage = lazy(() => import('./pages/DataManagementPage'));
const ZhiPage = lazy(() => import('./pages/ZhiPage'));
const CareerAdvicePage = lazy(() => import('./pages/CareerAdvicePage'));
const DailyFortunePage = lazy(() => import('./pages/DailyFortunePage'));
const LoveMatchPage = lazy(() => import('./pages/LoveMatchPage'));
const SelectDatePage = lazy(() => import('./pages/SelectDatePage'));
const FortuneCyclePage = lazy(() => import('./pages/FortuneCyclePage'));
const LifeGuidePage = lazy(() => import('./pages/LifeGuidePage'));
const EnergyDiaryPage = lazy(() => import('./pages/EnergyDiaryPage'));
const StudyAdvicePage = lazy(() => import('./pages/StudyAdvicePage'));
const InvestmentAdvicePage = lazy(() => import('./pages/InvestmentAdvicePage'));
const TimingPage = lazy(() => import('./pages/TimingPage'));
const BusinessMatchPage = lazy(() => import('./pages/BusinessMatchPage'));

/** 路由切换时的轻量加载指示器 */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="text-center">
        <div className="w-10 h-10 mx-auto mb-3 border-3 border-zhusha/20 border-t-zhusha rounded-full animate-spin" />
        <p className="text-xs text-stone/40">加载中...</p>
      </div>
    </div>
  );
}

function AppContent() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Navbar />
      <div className="flex-1 pb-12">
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
          {/* 公共路由（无需排盘） */}
          <Route path="/" element={<HomePage />} />
          <Route path="/input" element={<InputPage />} />
          <Route path="/result" element={<ResultPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/daily-sign" element={<DailySignPage />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/bone-weight" element={<BoneWeightPage />} />
          <Route path="/bone-weight-history" element={<BoneWeightHistoryPage />} />
            <Route path="/time-fix" element={<TimeFixPage />} />
            <Route path="/notifications" element={<NotificationSettingsPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/data-management" element={<DataManagementPage />} />
            <Route path="/zhi" element={<ZhiPage />} />

            {/* 需要排盘的功能路由 */}
            <Route path="/career" element={<RouteGuard><CareerAdvicePage /></RouteGuard>} />
            <Route path="/fortune" element={<RouteGuard><DailyFortunePage /></RouteGuard>} />
            <Route path="/love" element={<RouteGuard><LoveMatchPage /></RouteGuard>} />
            <Route path="/select-date" element={<RouteGuard><SelectDatePage /></RouteGuard>} />
            <Route path="/fortune-cycles" element={<RouteGuard><FortuneCyclePage /></RouteGuard>} />
            <Route path="/life-guide" element={<RouteGuard><LifeGuidePage /></RouteGuard>} />
            <Route path="/energy-diary" element={<RouteGuard><EnergyDiaryPage /></RouteGuard>} />
            <Route path="/study" element={<RouteGuard><StudyAdvicePage /></RouteGuard>} />
            <Route path="/invest" element={<RouteGuard><InvestmentAdvicePage /></RouteGuard>} />
            <Route path="/timing" element={<RouteGuard><TimingPage /></RouteGuard>} />
            <Route path="/business" element={<RouteGuard><BusinessMatchPage /></RouteGuard>} />
          </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
      {/* 底部常驻命盘栏 */}
      <BaziBar />
      {/* 全局隐私说明 */}
      <footer className="py-3 text-center pb-12">
        <div className="flex items-center justify-center gap-1 text-[10px] text-stone/35 flex-wrap">
          <span>🔒</span>
          <span>不卖课 · 不接广告 · 不泄露隐私</span>
          <span className="mx-1">·</span>
          <Link to="/privacy" className="underline hover:text-stone/50 transition-colors">
            隐私政策
          </Link>
          <span className="mx-1">·</span>
          <Link to="/terms" className="underline hover:text-stone/50 transition-colors">
            服务条款
          </Link>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BaziProvider>
        <AppContent />
      </BaziProvider>
    </AuthProvider>
  );
}

export default App;
